import { useMemo } from 'react';
import clsx from 'clsx';
import { AmountUtils, DateUtils } from '@shared/utils';
import { DepositPaymentStatusEnum, DepositPaymentTypeOfTransferEnum, type GetDepositPaymentsResponse } from '@shared/api/rest/payment';
import { PaymentTransactionColumns } from '../../models';
import { Table, TableCellType } from '@shared/widgets';

const getNamingTypeOfTransfer = (type: DepositPaymentTypeOfTransferEnum) => {
  if (type === DepositPaymentTypeOfTransferEnum.CardAccountSettlement) return 'Card Account Settlement';
  if (type === DepositPaymentTypeOfTransferEnum.OtherBankTransfer) return 'Other Bank Transfer';
  if (type === DepositPaymentTypeOfTransferEnum.OtherInsigniaAccount) return 'Other Insignia Account';
  if (type === DepositPaymentTypeOfTransferEnum.OwnInsigniaAccount) return 'Own Insignia Account';
  if (type === DepositPaymentTypeOfTransferEnum.BetweenInsigniaAccount) return 'Between Insignia Account';
  else return 'Unknown';
};

interface IProps {
  isLoading?: boolean;
  columns: string[];
  items: GetDepositPaymentsResponse[];
  currentPage: number;
  totalItemCount: number;
  countItemPage: number;
  onChangePage: (pageNumber: number) => void;
  onChangeCountItemPage: (pageNumber: number) => void;
}
export const DepositTransactionTable = (props: IProps) => {
  const cells: Record<
    string,
    (row: GetDepositPaymentsResponse) => {
      type: TableCellType;
      value: React.ReactNode;
    }
  > = useMemo(
    () => ({
      Status: (row: GetDepositPaymentsResponse) => {
        const isCompleted = row.status === DepositPaymentStatusEnum.Completed;
        const isFailed = row.status === DepositPaymentStatusEnum.Failed || row.status === DepositPaymentStatusEnum.Rejected;
        return {
          type: TableCellType.Custom,
          value: (
            <div
              className={clsx(
                'py-1 px-2 text-center font-normal',
                isCompleted ? 'bg-dark-emerald text-emerald' : isFailed ? 'bg-light-carmine-pink text-purple-pink' : 'bg-dark-red-sand text-red-sand',
              )}
            >
              {isCompleted ? 'Completed' : isFailed ? 'Failed' : 'Processing'}
            </div>
          ),
        };
      },
      ReferenceNumber: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.referenceNumber,
      }),
      Date: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: DateUtils.formatDate(row.date),
      }),
      AccountNumber: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.accountNumber,
      }),
      Direction: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.direction,
      }),
      TypeOfTransfer: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: getNamingTypeOfTransfer(row.typeOfTransfer),
      }),
      Description: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.description,
      }),
      SenderIban: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.senderIban,
      }),
      BeneficiaryIban: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.beneficiaryIban,
      }),
      ExchangeRate: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: `${AmountUtils.transformToAmountWithCurrency(1, row.currencyIn)} = ${AmountUtils.transformToAmountWithCurrency(row.exchangeRate, row.currencyOut)}`,
      }),
      SenderAmount: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: AmountUtils.transformToAmountWithCurrency(row.senderAmount, row.currencyIn),
      }),
      BeneficiaryAmount: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: AmountUtils.transformToAmountWithCurrency(row.beneficiaryAmount, row.currencyOut),
      }),
      Fee: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: AmountUtils.transformToAmountWithCurrency(row.fee, row.currency),
      }),
      TotalAmount: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: AmountUtils.transformToAmountWithCurrency(row.totalAmount, row.currency),
      }),
    }),
    [],
  );
  const items = useMemo(() => {
    return props.items.map((row) => {
      return props.columns.reduce((acc: { type: TableCellType; value: React.ReactNode }[], col) => {
        const cell = cells[col]?.(row);
        cell && acc.push(cell);
        return acc;
      }, []);
    });
  }, [cells, props.columns, props.items]);
  return (
    <Table
      columns={props.columns.map((column) => PaymentTransactionColumns[column as keyof typeof PaymentTransactionColumns])}
      items={items}
      pagination={{
        currentPage: props.currentPage,
        totalItemCount: props.totalItemCount,
        countItemPage: props.countItemPage,
        onChangePage: props.onChangePage,
        onChangeCountItemPage: props.onChangeCountItemPage,
      }}
      isLoading={props.isLoading}
      isScrollable
    />
  );
};
